<template>
  <VDialog
    data-testid="confirm-delete-dialog"
    :model-value="showDeleteDialog"
    max-width="600px"
    :transition="false"
    attach="#app"
  >
    <VCard class="p-4">
      <VCardTitle class="flex justify-space-between items-center p-0">
        <div class="text-h4 p-4">
          {{ t('Delete consultation') }}
        </div>
        <VBtn
          icon="close"
          variant="plain"
          class="button-small mr-2"
          :disabled="deleteAction?.disable"
          size="small"
          :ripple="false"
          data-testid="btn-close"
          @click="showDeleteDialog = false"
        />
      </VCardTitle>
      <VCardText class="text-body-1 p-4">
        {{ t('Do you really want to delete the consultation request?') }}
      </VCardText>
      <template #actions>
        <VCardActions>
          <VBtn
            variant="text"
            color="primary"
            data-testid="btn-cancel"
            :disabled="deleteAction?.disable"
            class="ml-auto button-large"
            :text="t('Cancel')"
            @click="showDeleteDialog = false"
          />
          <VBtn
            variant="elevated"
            color="primary"
            class="ml-4 button-large"
            :disabled="deleteAction?.disable"
            :text="t('Delete consultation')"
            data-testid="btn-confirm"
            @click="deleteAction?.actionCallback()"
          />
        </VCardActions>
      </template>
    </VCard>
  </VDialog>

  <VRow
    v-if="showActionButtons"
    data-testid="receiveractionbuttons"
    no-gutters
    class="flex gap-3 justify-end items-end flex-wrap"
  >
    <template v-for="(action, index) in consultationActionsPerStatus">
      <VBtn
        v-if="action.showAction"
        :key="index"
        :disabled="action.disable"
        :data-testid="`${action.actionName}-btn`"
        :variant="setButtonVariant(action.type, index)"
        :class="[
          'button-medium',
          index === consultationActionsPerStatus.length - 1 ? '' : 'border-[#D5D7DA]'
        ]"
        :color="setButtonColor(action.type, index)"
        :loading="action.isLoading"
        @click.stop.prevent="handleCallbackAction(action)"
      >
        {{ action.actionName }}
      </VBtn>
    </template>
    <!--
    TODO TSDC-1508: Review and decide on next steps for 'accept-btn' feature.
    Code is currently disabled to prevent user confusion.
    See ticket for full context. Do not re-enable or remove without team agreement.
    -->
    <!--
    <VBtn
      v-if="userCanAccept"
      :disabled="hasGodModeShift"
      data-testid="accept-btn"
      variant="outlined"
      color="primary"
      class="button-medium"
      @click.stop.prevent="acceptConsultationRequest"
    >
      {{ t('I will call back') }}
    </VBtn>
    -->
  </VRow>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import useConsultationActions, { type Action } from '@/composables/useConsultationActions'
import { useI18n } from 'vue-i18n'
import type { ConsultationRequestDTO } from '@/generated'
import { ActionType } from '@/enums/ActionType'

const { consultation, showActionButtons = true } = defineProps<{
  consultation: ConsultationRequestDTO
  showActionButtons?: boolean
}>()

const { t } = useI18n()

const reactiveConsultation = toRef(() => consultation)

const showDeleteDialog = ref<boolean>(false)
const { consultationActionsPerStatus } = useConsultationActions(reactiveConsultation)
const deleteAction = computed(() =>
  consultationActionsPerStatus.value.find((action) => action.type === ActionType.DELETE)
)

function handleCallbackAction(action: Action) {
  if (action.type === ActionType.DELETE) {
    showDeleteDialog.value = true
  } else {
    action.actionCallback()
  }
}

function setButtonColor(actionType: ActionType, index: number) {
  if (actionType === ActionType.DELETE) {
    return 'error'
  }

  if (index === consultationActionsPerStatus.value.length - 1) {
    return 'primary'
  }

  return undefined
}

function setButtonVariant(actionType: ActionType, index: number) {
  if (actionType === ActionType.DELETE || index !== consultationActionsPerStatus.value.length - 1) {
    return 'outlined'
  }

  return 'flat'
}

// const {
//   // userCanAccept, TODO TSDC-1508: Review and decide on next steps for 'accept-btn' feature.
//   userCanDelete
//   // acceptConsultationRequest TODO TSDC-1508: Review and decide on next steps for 'accept-btn' feature.
// } = useConsultationRequest(consultation)
</script>

<style scoped></style>
